import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../componentsII/Buttons";
import { useForm, Controller } from "react-hook-form";
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomCard from "../../componentsII/Card"
import api from "../../axios/api";
import { showNotification } from "../../redux/slices/notificationSlice";
import ConfirmationModal from "../../componentsII/ConfirmationModal";

const ElectricityConsumptionDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [fuelUnit, setFuelUnit] = useState([]);
  const [consumptionType, setConsumptionType] = useState(initialData?.electricity_consumption?.consumption_type || "USAGE_BASE");
  const year = useSelector((state) => state.user.selectedYear);
  const user = useSelector((state) => state.user.user);

  const schema = Yup.object().shape({
    electricity_consumption_type: Yup.string().required("This field is required"),
    ...(consumptionType === "USAGE_BASE" ? {
      electricity_qty: Yup.number().required("Electricity consumed is required"),
      qty_unit_id: Yup.number().required("Unit is required").test('not-zero', 'This field is required', value => value !== 0),
    } : {})
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData.electricity_consumption?.electricity_consumption_id || 0,
      electricity_qty: detailModalData.electricity_consumption?.consumption_type === "ESTIMATION" 
        ? 1146.37 
        : detailModalData.electricity_consumption?.electricity_qty || null,
      qty_unit_id: detailModalData.electricity_consumption?.consumption_type === "ESTIMATION"
        ? 17
        : detailModalData.electricity_consumption?.qty_unit_id || 0,
      electricity_consumption_type: detailModalData.electricity_consumption?.consumption_type || "USAGE_BASE",
      location_id: detailModalData.location_id || null,
      user_id: user.id || null
    },
  });

  // Watch for changes in consumption type
  const selectedConsumptionType = watch("electricity_consumption_type");

  useEffect(() => {
    setConsumptionType(selectedConsumptionType);
  }, [selectedConsumptionType]);

  const getConsumptionUnit = async () => {
    try {
      // const res = await api.post(`/common/measurementType`, { type_code: ["ELECTRICITY"] });
      const res = await api.get(`/common/measurementType?type_code=ELECTRICITY`);
      setFuelUnit(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const handleEditSubmit = async (data) => {
    const body = {
      ...data,
      electricity_qty: data.electricity_consumption_type === "ESTIMATION" ? 1146.37 : (data.electricity_qty || null),
      qty_unit_id: data.electricity_consumption_type === "ESTIMATION" ? 17 : (data.qty_unit_id || null),
    };
  
    try {
      const response = await api.post(
        "/company/addUpdateElectricityConsumption",
        body
      );
      if (response.data.status === "200") {
        dispatch(
          showNotification({ message: response.data.message, type: "success" })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({ message: response.data.message, type: "error" })
        );
        setIsEditable(false);
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message,
          type: "error",
        })
      );
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "electricity_consumption_type") {
        if (value.electricity_consumption_type === "ESTIMATION") {
          reset({
            ...value,
            electricity_qty: 1146.37,
            qty_unit_id: 17
          });
        } else {
          reset({
            ...value,
            electricity_qty: null,
            qty_unit_id: null
          });
        }
      }
    });
  
    return () => subscription.unsubscribe();
  }, [watch, reset]);

  useEffect(() => {
    getConsumptionUnit();
  }, []);

  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/company/deleteElectricityConsumption/${detailModalData?.electricity_consumption.electricity_consumption_id}`
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
      }
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const renderConsumptionFields = () => {
    if (consumptionType === "USAGE_BASE") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <CompInput
            title={`How much Energy did ${detailModalData.loc_name} used in ${year.start_year}?`}
            value={initialData.electricity_qty}
            name="electricity_qty"
            isError={!!errors.electricity_qty}
            errorText={errors.electricity_qty?.message}
            control={control}
            isEditable={isEditable}
            type="number"
          />
          <CompSelect
            title="Unit"
            value={initialData.qty_unit_id}
            name="qty_unit_id"
            isError={!!errors.qty_unit_id}
            errorText={errors.qty_unit_id?.message}
            control={control}
            isEditable={isEditable}
            options={fuelUnit}
          />
        </div>
      );
    }

    return (
      <div className="flex justify-center items-center bg-white">
        <div className="flex items-center space-x-4 pb-4 p-5 rounded-lg w-2/3 shadow-lg shadow-gray-400 bg-white">
          <div className="text-center">
            <div className="text-xl font-bold">1,146.37 <span className="font-normal">tCO2e</span></div>
            <div className="border-b border-gray-800 h-2 mx-4"></div>
            <div className="text-sm mt-2">Total Estimated Emissions</div>
          </div>

          <div className="border-l border-gray-500 h-40 mx-4"></div>

          <div className="flex-1">
            <p>
              We will use these estimates to calculate your carbon footprint for purchased electricity for this location.
              These estimates are based off of <u>CBECS</u> and cannot be guaranteed to be accurate.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      <div>
        <div className="flex border-b border-dashed border-gray-500">
          <div className="px-8 w-full text-center">
            <h1 className="text-2xl font-bold text-green-600">
              {initialData?.electricity_consumption?.electricity_consumption_id ? "Edit" : "Add"} {year?.start_year} Electricity consumption data
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium">
              To add your related electricity usage, you will need the amount of electricity consumed (e.g. 230 kWh) during your reporting period.
              If you do not have actual usage data, you can use estimated emissions based on the purpose of the location and floor area.
            </p>
          </div>
        </div>

        <div className="px-6">
          <div className="flex justify-center w-full items-center py-6">
            <div className="w-[60%] rounded-[20px] border-[10px] shadow-md">
              <CustomCard
                image={detailModalData.img}
                title={detailModalData.loc_name}
                subtitleI={`${detailModalData?.address_line_1}`}
                subtitleII={detailModalData?.city_name}
                subtitleIII={detailModalData?.veh_subtype_details?.sub_type_name}
              />
            </div>
          </div>
          <form onSubmit={handleSubmit(handleEditSubmit)}>
            <div className="flex justify-center items-center w-50%">
              <CompRadioButton
                title="How would you like to calculate Electricity Emissions?"
                name="electricity_consumption_type"
                value={initialData.electricity_consumption_type}
                control={control}
                isError={!!errors.electricity_consumption_type}
                layout="column"
                errorText={errors.electricity_consumption_type?.message}
                options={[
                  { value: "USAGE_BASE", label: 'Use Actual Data', helperText: 'This is more accurate and will make you more likely to pass audits.' },
                  { value: "ESTIMATION", label: 'Use Estimates' },
                ]}
              />
            </div>

            {renderConsumptionFields()}

            <div className="flex justify-between items-center w-full py-2">
              {initialData?.electricity_consumption?.electricity_consumption_id && (
                <div>
                  <CustomButton
                    className="bg-red-500 hover:bg-red-700 text-white"
                    click={() => setIsDeleteModalOpen(true)}
                    text="Delete"
                  />
                </div>
              )}

              <div className="w-full flex justify-end gap-5 items-center py-5">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white"
                  click={() => handleCloseMainDetailModal()}
                  text="Cancel"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white"
                  type="submit"
                  text="Submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Electricity Usage</div>}
      />
    </div>
  );
};

export default ElectricityConsumptionDetailModal;


const CompInput = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  required=true,
  type="text"
}) => {
  return (
    <div className={`h-16 w-full items-center`}>
      <div className="font-medium text-gray-900 flex gap-1 ">
        {title}
        <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
      </div>   
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            type={type}
            {...field}
            fullWidth
            error={isError}
            helperText={errorText}
            margin="none"
            placeholder="Enter Value"
            InputProps={{
              disableUnderline: true,
              sx: {
                padding: 0,
                "& .MuiInputBase-input": {
                  padding: "10px",
                },
              },
            }}
            sx={{
              padding: 0,
              margin: 0,
            }}
            onBlur={(e) => {
              if(type == "number") {
                const formattedValue = parseFloat(e.target.value).toFixed(2);
                field.onChange(formattedValue);
              }
            }}
          />
        )}
      />
    </div>
  );
};

const CompSelect = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  options,
  required=true,
  handleChange = () => { },
}) => {
  return (
    <div className={`h-16 w-full items-center`}>
      <div className="font-medium text-gray-900 flex gap-1 ">
        {title}
        <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
      </div>   
      <div className="">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={isError}>
              <Select
                {...field}
                sx={{
                  padding: 0, // Removes padding in the Select component itself
                  "& .MuiInputBase-input": {
                    padding: "10px !important", // Removes padding from the input area
                  },
                }}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e.target.value);
                }}
                renderValue={(selected) => {
                  const selectedVal = options.find((item) => item?.value == selected)
                  if (selectedVal == undefined) {
                    return <em>Select option</em>;
                  }
                  return selectedVal?.label;
                }}
              >
                <MenuItem key={1100000} value="" disabled>
                  Select option
                </MenuItem>
                {options.map((make) => (
                  <MenuItem key={make.value} value={make.value}>
                    {make.label}
                  </MenuItem>
                ))}
              </Select>
              {errorText &&   <FormHelperText className="text-red-500 mt-1">{errorText}</FormHelperText>}
            </FormControl>
          )}
        />
      </div>
    </div>
  );
};

const CompRadioButton = ({
  title,
  value,
  control,
  isError,
  errorText,
  name,
  options,
  handleChange = () => { },
}) => {
  return (
    <div className="w-full mb-2">
      <div className="font-medium text-gray-900">{title}</div>
      <div>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <FormControl error={isError} className="w-full">
              <RadioGroup
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e.target.value);
                }}
              >
                <div className="flex flex-col ">
                  {options.map((option) => (
                    <div key={option.value}>
                      <FormControlLabel
                        value={option.value}
                        control={
                          <Radio
                            sx={{
                              color: 'gray',
                              '&.Mui-checked': {
                                color: 'rgb(22 163 74)', // green-600
                              },
                            }}
                          />
                        }
                        label={option.label}
                        className={`
                          m-0 
                          rounded-md
                          transition-colors
                          duration-200
                          hover:bg-green-50
                          w-full
                          p-2
                          ${field.value === option.value ? 'bg-green-50' : ''}
                        `}
                      />
                      {option.helperText && (
                        <div className="text-sm text-gray-500 pl-10 p-0 -mt-5">
                          {option.helperText}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </RadioGroup>
              {errorText && (
                <FormHelperText className="text-red-500 mt-1">{errorText}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </div>
    </div>
  );
};
